import React from 'react';
import { WifiOff, Clock, AlertCircle, RefreshCw, Home, ServerCrash } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { categorizeError } from '../utils/networkError';

/**
 * ApiErrorAlert component
 * Displays appropriate error message based on error type (timeout, network, server, or other)
 */
const ApiErrorAlert = ({ error, onRetry, isRetrying = false, showHomeButton = true }) => {
  const navigate = useNavigate();
  const errorInfo = categorizeError(error);

  // Determine icon and styling based on error type
  const getErrorConfig = () => {
    switch (errorInfo.type) {
      case 'timeout':
        return {
          Icon: Clock,
          title: 'Request Timeout',
          bgColor: 'bg-yellow-500/10',
          borderColor: 'border-yellow-500/30',
          textColor: 'text-yellow-400',
          textColorLight: 'text-yellow-300/80',
          buttonBg: 'bg-yellow-500/20 hover:bg-yellow-500/30',
          buttonBorder: 'border-yellow-500/40',
          buttonText: 'text-yellow-300',
        };
      case 'network':
        return {
          Icon: WifiOff,
          title: 'Connection Error',
          bgColor: 'bg-orange-500/10',
          borderColor: 'border-orange-500/30',
          textColor: 'text-orange-400',
          textColorLight: 'text-orange-300/80',
          buttonBg: 'bg-orange-500/20 hover:bg-orange-500/30',
          buttonBorder: 'border-orange-500/40',
          buttonText: 'text-orange-300',
        };
      case 'server':
        return {
          Icon: ServerCrash,
          title: 'Server Error',
          bgColor: 'bg-purple-500/10',
          borderColor: 'border-purple-500/30',
          textColor: 'text-purple-400',
          textColorLight: 'text-purple-300/80',
          buttonBg: 'bg-purple-500/20 hover:bg-purple-500/30',
          buttonBorder: 'border-purple-500/40',
          buttonText: 'text-purple-300',
        };
      default:
        return {
          Icon: AlertCircle,
          title: 'Error',
          bgColor: 'bg-red-500/10',
          borderColor: 'border-red-500/30',
          textColor: 'text-red-400',
          textColorLight: 'text-red-300/80',
          buttonBg: 'bg-red-500/20 hover:bg-red-500/30',
          buttonBorder: 'border-red-500/40',
          buttonText: 'text-red-300',
        };
    }
  };

  const handleReturnHome = () => {
    navigate('/');
  };

  const config = getErrorConfig();
  const { Icon } = config;

  return (
    <div
      className={`mb-6 p-4 ${config.bgColor} border ${config.borderColor} rounded-xl`}
      role="alert"
      aria-live="polite"
    >
      <div className="flex items-start gap-3">
        <Icon className={`w-5 h-5 ${config.textColor} flex-shrink-0 mt-0.5`} aria-hidden="true" />
        <div className="flex-1">
          <p className={`${config.textColor} text-sm font-medium mb-2`}>
            {config.title}
          </p>
          <p className={`${config.textColorLight} text-sm mb-3`}>
            {errorInfo.message}
          </p>
          <div className="flex flex-wrap gap-3">
            {errorInfo.isRecoverable && onRetry && (
              <button
                type="button"
                onClick={onRetry}
                disabled={isRetrying}
                className={`inline-flex items-center gap-2 px-4 py-2 ${config.buttonBg} border ${config.buttonBorder} rounded-lg ${config.buttonText} text-sm font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed`}
                aria-label={isRetrying ? 'Retrying...' : 'Retry'}
              >
                <RefreshCw
                  className={`w-4 h-4 ${isRetrying ? 'animate-spin' : ''}`}
                  aria-hidden="true"
                />
                {isRetrying ? 'Retrying...' : 'Retry'}
              </button>
            )}
            {showHomeButton && errorInfo.type === 'server' && (
              <button
                type="button"
                onClick={handleReturnHome}
                className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg text-white/80 text-sm font-medium transition-all duration-200"
                aria-label="Return Home"
              >
                <Home className="w-4 h-4" aria-hidden="true" />
                Return Home
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApiErrorAlert;
